import React, { useRef, useEffect, useState } from 'react';
import Slide from './Slide';
import { templates } from './templates';

const CarouselGenerator = () => {
  const [mode, setMode] = useState('text');
  const [inputText, setInputText] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [warning, setWarning] = useState(null);  
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [useCustomColors, setUseCustomColors] = useState(true);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Global color states
  const [titleBgColor, setTitleBgColor] = useState('#000000');
  const [titleTextColor, setTitleTextColor] = useState('#ffffff');
  const [contentBgColor, setContentBgColor] = useState('#000000');
  const [contentTextColor, setContentTextColor] = useState('#ffffff');

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const containerWidth = entry.contentRect.width;
      const containerHeight = entry.contentRect.height;
      
      // For a 4:5 aspect ratio (1080:1350)
      const targetWidth = 1080;
      const targetHeight = 1350;
      
      // Calculate the max scale that will fit both dimensions
      const widthScale = containerWidth / targetWidth;
      const heightScale = containerHeight / targetHeight;
      
      // Use the smaller scale to ensure the slide fits completely
      const newScale = Math.min(widthScale, heightScale) * 0.95; // 0.95 adds a small margin
      
      setScale(newScale);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Toggle edit mode on/off for a slide by its id
  const toggleEditing = (id) => {
    setSlides((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isEditing: !s.isEditing } : s))
    );
  };

  // Update a specific field ("headline" or "body") for a slide
  const updateSlideField = (id, field, newValue) => {
    setSlides((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: newValue } : s))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // choose which field to send
      const payload =
        mode === 'text'
          ? { text: inputText }
          : { youtube_url: youtubeUrl };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();

      // display backend warning if any
      setWarning(data.warning || null);

      // update slides as before
      setSlides(
        (data.slides || []).map((slide, idx) => ({
          id: idx,
          ...slide,
          isEditing: false,
        }))
      );
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error generating slides:', error);
    }
  };

  const handlePrevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextSlide = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, slides.length - 1));
  };

  return (
    <div className="flex h-min">
      {/* LEFT HALF: input form & editing controls */}
      <div className="w-1/2 p-8 border-r overflow-y-auto">
        {/* Template Picker (unchanged) */}
        <div className="mb-4">
          <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
            Choose Template:
          </label>
          <select
            id="template"
            value={selectedTemplate.id}
            onChange={e => {
              const tmpl = templates.find(t => t.id === e.target.value);
              setSelectedTemplate(tmpl);
            }}
            className="w-full border rounded px-2 py-1"
          >
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* MODE TOGGLE */}
          <div className="mb-4 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="mode"
                value="text"
                checked={mode === 'text'}
                onChange={() => setMode('text')}
                className="mr-1"
              />
              Text
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="mode"
                value="youtube"
                checked={mode === 'youtube'}
                onChange={() => setMode('youtube')}
                className="mr-1"
              />
              YouTube
            </label>
          </div>

          {/* CONDITIONAL INPUT */}
          {mode === 'text' ? (
            <textarea
              className="mb-4 p-2 border rounded w-full"
              rows="6"
              placeholder="Enter your text here..."
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                setWarning(null);
              }}
            />
          ) : (
            <input
              type="url"
              className="mb-4 p-2 border rounded w-full"
              placeholder="Enter YouTube URL..."
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.target.value);
                setWarning(null);
              }}
            />
          )}

          {/* TRUNCATION / FETCH WARNING */}
          {warning && (
            <p className="mb-4 text-yellow-700">
              ⚠️ {warning}
            </p>
          )}
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
            Generate Slides
          </button>
        </form>

        {/* Editing panel (shown only if slides exist) */}
        {slides.length > 0 && (
          <div className="mt-8 border-t pt-4">
            <h2 className="text-lg font-semibold mb-2">Edit Current Slide</h2>
            <div className="flex items-center mb-2">
              <button
                onClick={() => toggleEditing(slides[currentIndex].id)}
                className="bg-gray-700 text-white text-xs px-2 py-1 rounded mr-2"
              >
                {slides[currentIndex].isEditing ? "Done" : "Edit"}
              </button>
              <span className="text-sm text-gray-600">
                Slide {currentIndex + 1} of {slides.length}
              </span>
            </div>
            {/* If the slide is in editing mode, show separate fields for headline and body */}
            {slides[currentIndex].isEditing && (
              <>
                <textarea
                  className="w-full p-2 border rounded mb-2"
                  rows="2"
                  value={slides[currentIndex].headline}
                  onChange={(e) =>
                    updateSlideField(slides[currentIndex].id, "headline", e.target.value)
                  }
                  placeholder="Edit headline..."
                />
                <textarea
                  className="w-full p-2 border rounded"
                  rows="3"
                  value={slides[currentIndex].body}
                  onChange={(e) =>
                    updateSlideField(slides[currentIndex].id, "body", e.target.value)
                  }
                  placeholder="Edit body..."
                />
              </>
            )}

            {/* Custom‑color toggle */}
            <div className="mb-4 flex items-center space-x-2">
              <input
                id="useCustomColors"
                type="checkbox"
                checked={useCustomColors}
                onChange={e => setUseCustomColors(e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="useCustomColors" className="text-sm">
                Use custom colors
              </label>
            </div>

            {/* Color controls always visible */}
            {/* ————— TITLE SLIDE CONTROLS ————— */}
            <h3 className="font-medium mb-2">Title Slide Colors</h3>

            <div className="mb-6 flex space-x-8">
              {/* Title Background */}
              <div className="flex flex-col">
                <label className="block text-sm mb-1">Background</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={titleBgColor}
                    onChange={e => setTitleBgColor(e.target.value)}
                    className="w-8 h-8 border rounded"
                  />
                  <input
                    type="text"
                    value={titleBgColor}
                    onChange={e => setTitleBgColor(e.target.value)}
                    className="w-20 p-1 border rounded text-xs"
                    placeholder="#rrggbb"
                  />
                </div>
              </div>

              {/* Title Text */}
              <div className="flex flex-col">
                <label className="block text-sm mb-1">Text</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={titleTextColor}
                    onChange={e => setTitleTextColor(e.target.value)}
                    className="w-8 h-8 border rounded"
                  />
                  <input
                    type="text"
                    value={titleTextColor}
                    onChange={e => setTitleTextColor(e.target.value)}
                    className="w-20 p-1 border rounded text-xs"
                    placeholder="#rrggbb"
                  />
                </div>
              </div>
            </div>
            <hr className="my-4 border-gray-300"/>

            {/* ————— CONTENT/CTA SLIDE CONTROLS ————— */}
            <h3 className="font-medium mb-2">Content/CTA Slide Colors</h3>

            <div className="mb-6 flex space-x-8">
              {/* Content Background */}
              <div className="flex flex-col">
                <label className="block text-sm mb-1">Background</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={contentBgColor}
                    onChange={e => setContentBgColor(e.target.value)}
                    className="w-8 h-8 border rounded"
                  />
                  <input
                    type="text"
                    value={contentBgColor}
                    onChange={e => setContentBgColor(e.target.value)}
                    className="w-20 p-1 border rounded text-xs"
                    placeholder="#rrggbb"
                  />
                </div>
              </div>

              {/* Content Text */}
              <div className="flex flex-col">
                <label className="block text-sm mb-1">Text</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={contentTextColor}
                    onChange={e => setContentTextColor(e.target.value)}
                    className="w-8 h-8 border rounded"
                  />
                  <input
                    type="text"
                    value={contentTextColor}
                    onChange={e => setContentTextColor(e.target.value)}
                    className="w-20 p-1 border rounded text-xs"
                    placeholder="#rrggbb"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT HALF: slide display and navigation */}
      <div className="w-1/2 flex flex-col items-center justify-center p-4">
        <div 
          ref={containerRef}
          className="w-full max-w-[600px] aspect-[4/5] relative overflow-hidden"
        >
          {slides.length > 0 ? (
            <div
              style={{
                width: 1080,
                height: 1350,
                transform: `scale(${scale})`,
                transformOrigin: 'center',
                position: 'absolute',
                top: '50%',
                left: '50%',
                translate: '-50% -50%'
              }}
            >
              <Slide
                slide={slides[currentIndex]}
                isLast={currentIndex === slides.length - 1}
                template={selectedTemplate}
                titleBgColor={titleBgColor}
                titleTextColor={titleTextColor}
                contentBgColor={contentBgColor}
                contentTextColor={contentTextColor}
                useCustomColors={useCustomColors}
                className="w-[1080px] h-[1350px]"
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
              <p className="text-gray-500">No slides yet. Generate some!</p>
            </div>
          )}
        </div>
        
        {/* Navigation controls */}
        <div className="flex flex-col items-center">
          <div className="flex space-x-4">
            <button
              onClick={handlePrevSlide}
              disabled={currentIndex === 0}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              ←
            </button>
            <button
              onClick={handleNextSlide}
              disabled={currentIndex === slides.length - 1}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              →
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Slide {slides.length > 0 ? currentIndex + 1 : 0} of {slides.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselGenerator;
